import { Link } from 'react-router-dom';
import { formaterDate, formaterMontant } from '@gsm/partage';
import { useRessource } from '../api/ressource';
import { BandeauAlerte } from './BandeauAlerte';
import { Pastille, type TonPastille } from './Pastille';
import { Bloc, Tableau } from './Tableau';

export interface DemandeSortie {
  id: string;
  reference: string;
  montantDemande: string;
  totalite: boolean;
  motif: string | null;
  statut: string;
  dateDemande: string;
  dateServie: string | null;
  ordres: { reference: string }[];
}

const LIBELLE: Record<string, string> = {
  OUVERTE: 'Ouverte',
  ORDRES_GENERES: 'Ordres générés',
  SERVIE: 'Servie',
  ABANDONNEE: 'Abandonnée',
};

const TON: Record<string, TonPastille> = {
  OUVERTE: 'attente',
  ORDRES_GENERES: 'interface',
  SERVIE: 'succes',
  ABANDONNEE: 'neutre',
};

/**
 * Sorties de fonds d'un mandat.
 *
 * Depuis la bascule vers le back-office tiers, l'agence ne transmet plus de
 * demande de retrait à l'application : c'est le gérant qui décide de libérer un
 * montant, et le désinvestissement en découle. La demande se solde d'elle-même
 * quand le retrait apparaît dans le journal reçu.
 *
 * L'ouverture d'une demande se fait depuis Gestion › Sorties de fonds ; la
 * fiche ne fait que la suivre.
 */
export function BlocSortieFonds({ mandatId }: { mandatId: string }) {
  const demandes = useRessource<DemandeSortie[]>(`/demandes-sortie?mandatId=${mandatId}`);

  const enCours = (demandes.donnees ?? []).find(
    (d) => d.statut === 'OUVERTE' || d.statut === 'ORDRES_GENERES',
  );

  return (
    <Bloc
      titre="Sorties de fonds"
      meta={demandes.chargement ? 'chargement…' : `${demandes.donnees?.length ?? 0} demande(s)`}
    >
      {enCours && enCours.statut === 'OUVERTE' && (
        <BandeauAlerte nature="gestion" titre="Désinvestissement à préparer">
          {enCours.reference} attend ses ordres de vente.{' '}
          <Link to={`/mandats/${mandatId}/proposition`}>Générer la proposition</Link>.
        </BandeauAlerte>
      )}

      <Tableau
        colonnes={[
          { cle: 'ref', entete: 'Référence', rendu: (d: DemandeSortie) => d.reference },
          {
            cle: 'date',
            entete: 'Demandée le',
            rendu: (d: DemandeSortie) => formaterDate(d.dateDemande),
          },
          {
            cle: 'montant',
            entete: 'Montant',
            num: true,
            rendu: (d: DemandeSortie) =>
              d.totalite ? 'Totalité' : formaterMontant(d.montantDemande),
          },
          {
            cle: 'ordres',
            entete: 'Ordres',
            num: true,
            rendu: (d: DemandeSortie) => d.ordres.length || '—',
          },
          {
            cle: 'statut',
            entete: 'État',
            rendu: (d: DemandeSortie) => (
              <Pastille ton={TON[d.statut] ?? 'neutre'}>{LIBELLE[d.statut] ?? d.statut}</Pastille>
            ),
          },
          {
            cle: 'servie',
            entete: 'Servie le',
            rendu: (d: DemandeSortie) => (d.dateServie ? formaterDate(d.dateServie) : '—'),
          },
        ]}
        lignes={demandes.donnees ?? []}
        cle={(d) => d.id}
        vide="Aucune sortie de fonds."
      />

      <p className="consigne consigne--pied">
        Une demande se solde d’elle-même dès qu’un retrait du montant attendu apparaît dans la
        situation reçue du back-office : l’application constate le paiement, elle ne le déclenche
        pas.
      </p>
    </Bloc>
  );
}
